/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { ExamItemDetailViewModel } from "../types/ExamItemDetailViewModel.ts";
import { examTypeListValueEditorViewModelSchema } from "./examTypeListValueEditorViewModelSchema.ts";

export const examItemDetailViewModelSchema = z.object({
  id: z.optional(z.number().int().describe("ID da questão.")),
  componentType: z.optional(
    z
      .number()
      .int()
      .describe(
        "Tipo do componente da questão. Enum EComponentType: `1` = SingleChoice, `2` = MultipleChoice, `3` = SingleLineText, `4` = MultilineText, `5` = Numeric, `6` = Grade, `7` = Audio, `8` = Attachment.",
      ),
  ),
  cultureInfo: z
    .string()
    .describe(
      "Código de cultura do idioma da questão (ex: pt-BR, en-US). Opcional.",
    )
    .nullish(),
  label: z
    .string()
    .describe("Enunciado da questão (pode conter HTML). Opcional.")
    .nullish(),
  labelClean: z
    .string()
    .describe("Enunciado da questão sem formatação HTML. Opcional.")
    .nullish(),
  information: z
    .string()
    .describe(
      "Informação complementar exibida ao aluno junto ao enunciado. Opcional.",
    )
    .nullish(),
  filePath: z
    .string()
    .describe("Caminho do arquivo de mídia associado à questão. Opcional.")
    .nullish(),
  fileName: z
    .string()
    .describe("Nome do arquivo de mídia associado à questão. Opcional.")
    .nullish(),
  hasFile: z.optional(
    z
      .boolean()
      .describe("Indica se a questão possui um arquivo de mídia associado."),
  ),
  showLabel: z.optional(
    z
      .boolean()
      .describe("Define se o enunciado da questão será exibido ao aluno."),
  ),
  showInformation: z.optional(
    z
      .boolean()
      .describe("Define se a informação complementar será exibida ao aluno."),
  ),
  required: z.optional(
    z.boolean().describe("Define se a resposta à questão é obrigatória."),
  ),
  fixed: z.optional(
    z
      .boolean()
      .describe(
        "Define se a questão terá posição fixa na prova (não será randomizada).",
      ),
  ),
  pageBreak: z.optional(
    z
      .boolean()
      .describe(
        "Define se haverá quebra de página após esta questão na prova.",
      ),
  ),
  weight: z.optional(
    z.number().int().describe("Peso da questão na composição da nota final."),
  ),
  correctAnswer: z
    .string()
    .describe("Resposta correta esperada para questão numérica. Opcional.")
    .nullish(),
  active: z.optional(z.boolean().describe("Indica se a questão está ativa.")),
  teacherEvaluation: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a questão do tipo Grade requer avaliação do professor.",
      ),
  ),
  isTemplate: z.optional(
    z.boolean().describe("Indica se a questão é um template reutilizável."),
  ),
  randomizeAlternatives: z.optional(
    z
      .boolean()
      .describe(
        "Define se as alternativas da questão serão apresentadas em ordem aleatória.",
      ),
  ),
  cancelled: z.optional(
    z.boolean().describe("Indica se a questão foi cancelada."),
  ),
  cancelJustification: z
    .string()
    .describe("Justificativa para o cancelamento da questão. Opcional.")
    .nullish(),
  level: z.optional(
    z
      .number()
      .int()
      .describe(
        "Nível de dificuldade da questão. Enum EExamItemLevel: `1` = Simple, `2` = Complex.",
      ),
  ),
  status: z.optional(
    z
      .number()
      .int()
      .describe(
        "Status de aprovação da questão. Enum EExamItemStatus: `1` = Pending, `2` = Approved, `3` = Rejected.",
      ),
  ),
  statusJustification: z
    .string()
    .describe("Justificativa para o status de aprovação da questão. Opcional.")
    .nullish(),
  decimalPlaces: z
    .number()
    .int()
    .describe(
      "Número de casas decimais para questões numéricas. Valor entre 0 e 15. Opcional.",
    )
    .nullish(),
  max: z
    .number()
    .describe("Valor máximo aceito para resposta numérica. Opcional.")
    .nullish(),
  min: z
    .number()
    .describe("Valor mínimo aceito para resposta numérica. Opcional.")
    .nullish(),
  maxFileSizeInMegaBytes: z
    .number()
    .int()
    .describe(
      "Tamanho máximo em megabytes do arquivo de resposta aceito. Aplicável ao componente Attachment. Opcional.",
    )
    .nullish(),
  acceptedFormats: z
    .string()
    .describe(
      "Extensões de arquivo aceitas como resposta (formato serializado). Aplicável ao componente Attachment. Opcional.",
    )
    .nullish(),
  maxAudioTimeInSeconds: z
    .number()
    .int()
    .describe(
      "Tempo máximo em segundos para gravação de resposta em áudio. Aplicável ao componente Audio. Opcional.",
    )
    .nullish(),
  subtype: z
    .number()
    .int()
    .describe(
      "Subtipo da questão dissertativa. Aplicável apenas ao componente MultilineText. Opcional. Enum EExamItemSubtype: `1` = OpinionArticle, `2` = Essay, `3` = ModelAnswerEssay, `4` = MbaCase.",
    )
    .nullish(),
  modelAnswer: z
    .string()
    .describe(
      "Resposta-modelo (subtipos ModelAnswerEssay e MbaCase) ou notas de ensino (MbaCase) usadas como referência na correção. Máximo 1000 caracteres. Opcional.",
    )
    .nullish(),
  keyTerms: z
    .string()
    .describe(
      "Palavras-chave esperadas na resposta, separadas por vírgula. Aplicável ao subtipo ModelAnswerEssay. Máximo 1000 caracteres. Opcional.",
    )
    .nullish(),
  disciplineId: z
    .number()
    .int()
    .describe("ID da disciplina associada à questão. Opcional.")
    .nullish(),
  personAuthorId: z.optional(
    z.number().int().describe("ID do autor da questão."),
  ),
  companyId: z
    .number()
    .int()
    .describe("ID da empresa à qual a questão pertence. Opcional.")
    .nullish(),
  examLanguageId: z
    .number()
    .int()
    .describe("ID do idioma de prova associado à questão. Opcional.")
    .nullish(),
  examTypeListId: z
    .number()
    .int()
    .describe("ID da lista de alternativas associada à questão. Opcional.")
    .nullish(),
  parentExamItemId: z
    .number()
    .int()
    .describe("ID da questão-pai, quando esta é uma sub-questão. Opcional.")
    .nullish(),
  originExamItemId: z
    .number()
    .int()
    .describe(
      "ID da questão de origem, quando esta questão foi copiada de outra. Opcional.",
    )
    .nullish(),
  userCreation: z
    .string()
    .describe("Usuário responsável pela criação da questão. Opcional.")
    .nullish(),
  creationDate: z
    .string()
    .datetime()
    .describe("Data de criação da questão. Opcional.")
    .nullish(),
  userLastChange: z
    .string()
    .describe("Usuário responsável pela última alteração da questão. Opcional.")
    .nullish(),
  lastChangeDate: z
    .string()
    .datetime()
    .describe("Data da última alteração da questão. Opcional.")
    .nullish(),
  examTypeListValues: z
    .array(z.lazy(() => examTypeListValueEditorViewModelSchema))
    .describe(
      "Lista de alternativas da questão. Aplicável aos componentes SingleChoice e MultipleChoice. Opcional.",
    )
    .nullish(),
}) as unknown as ToZod<ExamItemDetailViewModel>;
